import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Trash2, FileDown, Eye, Pencil, Truck, Receipt, CheckCircle, Clock, ShieldCheck, MessageCircle, Mail, MailOpen, FileCode, Send, RefreshCw, AlertTriangle, PenLine, FileMinus } from 'lucide-react';
import { DOCUMENTO_LABELS } from '../../lib/documentiConst';

const SDI_BADGE_STYLE = {
  consegnata: 'text-emerald-600 border-emerald-500',
  accettata: 'text-emerald-600 border-emerald-500',
  inviata: 'text-sky-600 border-sky-500',
  scartata: 'text-red-600 border-red-500',
  rifiutata: 'text-red-600 border-red-500',
};

function StatoBadges({ doc, handlers }) {
  const statoSdi = (doc.tipo === 'fattura' || doc.tipo === 'nota_credito') && doc.dati_fattura?.stato_sdi;
  return (
    <>
      {doc.bozza && (
        <Badge className="ml-1 text-[10px] bg-amber-500 text-white uppercase" title="Documento incompleto salvato come bozza — aprilo con la matita per completarlo (il numero verrà assegnato al salvataggio)" data-testid={`badge-bozza-${doc.id}`}>
          <Pencil className="h-3 w-3 mr-1" /> Bozza
        </Badge>
      )}
      {doc.generata_da_contratto && (
        <Badge variant="outline" className="ml-1 text-[10px] text-violet-600 border-violet-500" title="Fattura generata automaticamente dal rinnovo del contratto di assistenza" data-testid={`badge-contratto-${doc.id}`}>
          <RefreshCw className="h-3 w-3 mr-1" /> Da contratto
        </Badge>
      )}
      {doc.generata_da_contratto && !doc.dati_fattura?.stato_sdi && (
        <Badge className="ml-1 text-[10px] bg-red-600 text-white animate-pulse" title="Fattura automatica NON ancora inviata allo SdI: scarica l'XML, caricalo su Aruba, poi clicca 'Segna inviata a SdI' (partirà l'email al cliente)" data-testid={`badge-sdi-mancante-${doc.id}`}>
          <AlertTriangle className="h-3 w-3 mr-1" /> Da inviare SdI
        </Badge>
      )}
      {statoSdi && (
        <Badge variant="outline" className={`ml-1 text-[10px] uppercase ${SDI_BADGE_STYLE[String(statoSdi).toLowerCase()] || 'text-slate-600 border-slate-400'}`} title={`Stato SdI (Aruba)${doc.dati_fattura?.sdi_ultimo_aggiornamento ? ' - aggiornato ' + new Date(doc.dati_fattura.sdi_ultimo_aggiornamento).toLocaleString('it-IT') : ''}`} data-testid={`badge-sdi-${doc.id}`}>
          SdI: {statoSdi}
        </Badge>
      )}
      {doc.tipo === 'fattura' && !doc.bozza && doc.dati_fattura?.pagato && (
        <Badge
          variant="outline"
          className="ml-1 text-[10px] text-emerald-600 border-emerald-500 cursor-pointer hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
          title={`Pagata${doc.dati_fattura?.data_pagamento ? ' il ' + new Date(doc.dati_fattura.data_pagamento).toLocaleDateString('it-IT') : ''} — clicca per segnare NON pagata`}
          onClick={(e) => { e.stopPropagation(); handlers.onTogglePagato(doc, false); }}
          data-testid={`pagato-off-${doc.id}`}
        >
          <CheckCircle className="h-3 w-3 mr-1" /> Pagata
        </Badge>
      )}
      {doc.tipo === 'fattura' && !doc.bozza && !doc.dati_fattura?.pagato && (
        <Badge
          variant="outline"
          className="ml-1 text-[10px] text-amber-600 border-amber-500 cursor-pointer hover:bg-amber-50 dark:hover:bg-amber-950/30"
          title="Da pagare (RiBa/bonifico in attesa) — clicca per segnare PAGATA"
          onClick={(e) => { e.stopPropagation(); handlers.onTogglePagato(doc, true); }}
          data-testid={`pagato-on-${doc.id}`}
        >
          <Clock className="h-3 w-3 mr-1" /> Da pagare
        </Badge>
      )}
      {doc.tipo === 'fattura' && !doc.bozza && doc.email_fattura_aperta_il && (
        <Badge variant="outline" className="ml-1 text-[10px] text-emerald-600 border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30" title={`Email della fattura APERTA dal cliente il ${new Date(doc.email_fattura_aperta_il).toLocaleString('it-IT')} (inviata a ${doc.email_fattura_destinatario || ''})`} data-testid={`badge-email-letta-${doc.id}`}>
          <MailOpen className="h-3 w-3 mr-1" /> Email letta
        </Badge>
      )}
      {doc.tipo === 'fattura' && !doc.bozza && doc.email_fattura_inviata_il && !doc.email_fattura_aperta_il && (
        <Badge variant="outline" className="ml-1 text-[10px] text-sky-600 border-sky-500" title={`Email inviata a ${doc.email_fattura_destinatario || ''} il ${new Date(doc.email_fattura_inviata_il).toLocaleString('it-IT')} — non risulta ancora aperta dal cliente`} data-testid={`badge-email-inviata-${doc.id}`}>
          <Mail className="h-3 w-3 mr-1" /> Email inviata
        </Badge>
      )}
      {(doc.tipo === 'preventivo' || doc.tipo === 'offerta') && doc.stato_accettazione === 'accettato' && (
        <Badge variant="outline" className="ml-1 text-[10px] text-emerald-600 border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30" title={doc.accettazione_tipo === 'cartacea' ? `Accettato con firma cartacea il ${doc.data_accettazione ? new Date(doc.data_accettazione).toLocaleDateString('it-IT') : ''}` : `Firmato da ${doc.nome_firmatario || ''} il ${doc.data_accettazione ? new Date(doc.data_accettazione).toLocaleDateString('it-IT') : ''}`} data-testid={`badge-accettato-${doc.id}`}>
          {doc.accettazione_tipo === 'cartacea' ? <PenLine className="h-3 w-3 mr-1" /> : <CheckCircle className="h-3 w-3 mr-1" />}
          {doc.accettazione_tipo === 'cartacea' ? 'Accettato (cartaceo)' : 'Accettato'}
        </Badge>
      )}
      {(doc.tipo === 'preventivo' || doc.tipo === 'offerta') && !doc.stato_accettazione && doc.prima_apertura_portale && (
        <Badge
          variant="outline"
          className="ml-1 text-[10px] text-indigo-600 border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30"
          title={`Visualizzato dal cliente${doc.numero_aperture_portale ? ` ${doc.numero_aperture_portale} ${doc.numero_aperture_portale === 1 ? 'volta' : 'volte'}` : ''}${doc.ultima_apertura_portale ? ' - ultima ' + new Date(doc.ultima_apertura_portale).toLocaleString('it-IT') : ''}`}
          data-testid={`badge-visualizzato-${doc.id}`}
        >
          <Eye className="h-3 w-3 mr-1" /> Visualizzato
        </Badge>
      )}
      {(doc.tipo === 'preventivo' || doc.tipo === 'offerta') && !doc.bozza && !doc.stato_accettazione && !doc.prima_apertura_portale && (
        <Badge variant="outline" className="ml-1 text-[10px] text-amber-600 border-amber-500">
          <Clock className="h-3 w-3 mr-1" /> In attesa
        </Badge>
      )}
    </>
  );
}

function AzioniDocumento({ doc, isAdmin, canEdit, canConverti, handlers }) {
  const isPreventivo = doc.tipo === 'preventivo' || doc.tipo === 'offerta';
  return (
    <div className="flex gap-1">
      <Button variant="ghost" size="icon" className="h-8 w-8 rounded-md text-primary" onClick={() => handlers.onPreview(doc.id)} title="Visualizza" data-testid={`preview-doc-${doc.id}`}>
        <Eye className="h-4 w-4" />
      </Button>
      <Button variant="ghost" size="icon" className="h-8 w-8 rounded-md" onClick={() => handlers.onDownloadPDF(doc.id, doc.tipo, doc.numero)} title="Scarica" data-testid={`download-doc-${doc.id}`}>
        <FileDown className="h-4 w-4" />
      </Button>
      {(doc.tipo === 'fattura' || doc.tipo === 'nota_credito') && (
        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-md text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-950/30" onClick={() => handlers.onDownloadXML(doc.id, doc.tipo, doc.numero)} title="Scarica XML FatturaPA (da caricare su Aruba/SdI)" data-testid={`xml-doc-${doc.id}`}>
          <FileCode className="h-4 w-4" />
        </Button>
      )}
      {(doc.tipo === 'fattura' || doc.tipo === 'nota_credito') && !doc.dati_fattura?.sdi_upload_filename && (
        doc.dati_fattura?.sdi_inviata_manualmente_il ? (
          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-md text-white bg-emerald-500 hover:bg-emerald-600" onClick={() => handlers.onSegnaSdi(doc, false)} title={`Inviata a SdI (segnata a mano il ${new Date(doc.dati_fattura.sdi_inviata_manualmente_il).toLocaleDateString('it-IT')}) — clicca per annullare`} data-testid={`sdi-manuale-off-${doc.id}`}>
            <CheckCircle className="h-4 w-4" />
          </Button>
        ) : (
          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-md text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/30" onClick={() => handlers.onSegnaSdi(doc, true)} title="Segna come inviata a SdI (l'ho già caricata a mano su Aruba)" data-testid={`sdi-manuale-on-${doc.id}`}>
            <CheckCircle className="h-4 w-4" />
          </Button>
        )
      )}
      {(doc.tipo === 'fattura' || doc.tipo === 'nota_credito') && !doc.dati_fattura?.sdi_upload_filename && (
        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-md text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-950/30" onClick={() => handlers.onInviaSdi(doc)} title="Invia a SdI tramite Aruba (fatturazione elettronica)" data-testid={`sdi-invia-${doc.id}`}>
          <Send className="h-4 w-4" />
        </Button>
      )}
      {(doc.tipo === 'fattura' || doc.tipo === 'nota_credito') && doc.dati_fattura?.sdi_upload_filename && (
        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-md text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-950/30" onClick={() => handlers.onAggiornaSdi(doc.id)} title="Aggiorna stato SdI da Aruba" data-testid={`sdi-aggiorna-${doc.id}`}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      )}
      {doc.tipo === 'fattura' && !doc.bozza && (
        doc.email_fattura_inviata_il ? (
          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-md text-white bg-blue-500 hover:bg-blue-600" onClick={() => handlers.onSendEmailFattura(doc)} title={`Fattura già inviata via email a ${doc.email_fattura_destinatario || 'cliente'} il ${new Date(doc.email_fattura_inviata_il).toLocaleDateString('it-IT')} — clicca per inviare di nuovo`} data-testid={`email-fattura-${doc.id}`}>
            <Mail className="h-4 w-4" />
          </Button>
        ) : (
          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-md text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/30" onClick={() => handlers.onSendEmailFattura(doc)} title="Invia la fattura al cliente via email (PDF in allegato)" data-testid={`email-fattura-${doc.id}`}>
            <Mail className="h-4 w-4" />
          </Button>
        )
      )}
      {doc.tipo === 'fattura' && !doc.bozza && !doc.nota_credito_id && (
        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-md text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30" onClick={() => handlers.onNotaCredito(doc)} title="Crea Nota di Credito collegata a questa fattura (storno integrale)" data-testid={`nota-credito-${doc.id}`}>
          <FileMinus className="h-4 w-4" />
        </Button>
      )}
      {isPreventivo && !doc.bozza && (
        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-md text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/30" onClick={() => handlers.onSendEmail(doc)} title="Invia preventivo al cliente via email (link al portale)" data-testid={`email-doc-${doc.id}`}>
          <Mail className="h-4 w-4" />
        </Button>
      )}
      {isPreventivo && !doc.bozza && (
        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-md text-green-600 hover:bg-green-50 dark:hover:bg-green-950/30" onClick={() => handlers.onSendWhatsApp(doc)} title="Invia al cliente via WhatsApp (link al portale di accettazione)" data-testid={`whatsapp-doc-${doc.id}`}>
          <MessageCircle className="h-4 w-4" />
        </Button>
      )}
      {isPreventivo && !doc.bozza && doc.stato_accettazione !== 'accettato' && (
        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-md text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-950/30" onClick={() => handlers.onAccettazioneCartacea(doc, false)} title="Il cliente ha firmato su carta? Segna come accettato con FIRMA CARTACEA (abilita fattura/DDT senza accettazione digitale)" data-testid={`accettazione-cartacea-${doc.id}`}>
          <PenLine className="h-4 w-4" />
        </Button>
      )}
      {isPreventivo && doc.stato_accettazione === 'accettato' && (
        <>
          {doc.accettazione_tipo === 'cartacea' ? (
            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-md text-white bg-teal-500 hover:bg-teal-600" onClick={() => handlers.onAccettazioneCartacea(doc, true)} title="Accettato con firma cartacea — clicca per annullare" data-testid={`accettazione-cartacea-off-${doc.id}`}>
              <PenLine className="h-4 w-4" />
            </Button>
          ) : (
            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-md text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/30" onClick={() => handlers.onDownloadCertificato(doc.id, doc.numero)} title="Scarica Certificato di Accettazione (prova legale)" data-testid={`cert-doc-${doc.id}`}>
              <ShieldCheck className="h-4 w-4" />
            </Button>
          )}
          {canConverti && (
            <>
              <Button variant="ghost" size="icon" className="h-8 w-8 rounded-md text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/30" onClick={() => handlers.onConverti(doc, 'ddt')} title="Genera DDT da questo preventivo" data-testid={`convert-ddt-${doc.id}`}>
                <Truck className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 rounded-md text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/30" onClick={() => handlers.onConverti(doc, 'fattura')} title="Genera Fattura da questo preventivo" data-testid={`convert-fattura-${doc.id}`}>
                <Receipt className="h-4 w-4" />
              </Button>
            </>
          )}
        </>
      )}
      {canEdit && (
        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-md text-amber-600" onClick={() => handlers.onEdit(doc)} title="Modifica" data-testid={`edit-doc-${doc.id}`}>
          <Pencil className="h-4 w-4" />
        </Button>
      )}
      {(isAdmin || (doc.bozza && canEdit)) && (
        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-md text-destructive" onClick={() => handlers.onDelete(doc.id)} title={doc.bozza ? 'Elimina bozza' : 'Elimina'} data-testid={`delete-doc-${doc.id}`}>
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

/** Tabella documenti con badge stato e azioni per riga. */
export default function DocumentiTable({ documenti, getClienteName, isAdmin, canEditDoc, canConverti = true, handlers }) {
  return (
    <div className="border border-border rounded-md overflow-x-auto">
      <table className="data-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Tipo</th>
            <th>Oggetto</th>
            <th className="hidden md:table-cell">Cliente</th>
            <th className="hidden lg:table-cell">Creato da</th>
            <th className="hidden md:table-cell">Importo</th>
            <th className="hidden lg:table-cell">Data</th>
            <th>Azioni</th>
          </tr>
        </thead>
        <tbody>
          {documenti.length === 0 ? (
            <tr>
              <td colSpan={8} className="text-center text-muted-foreground py-8">
                Nessun documento trovato
              </td>
            </tr>
          ) : (
            documenti.map((doc) => {
              const docConfig = DOCUMENTO_LABELS[doc.tipo] || DOCUMENTO_LABELS.preventivo;
              const DocIcon = docConfig.icon;
              return (
                <tr
                  key={doc.id}
                  className="hover:bg-muted transition-colors cursor-pointer"
                  onClick={() => handlers.onPreview(doc.id)}
                >
                  <td className="font-mono font-bold">{doc.bozza ? '—' : doc.numero}</td>
                  <td>
                    <Badge className={`${docConfig.color} text-white rounded-md text-[10px] font-mono uppercase`}>
                      <DocIcon className="h-3 w-3 mr-1" />
                      {docConfig.label}
                    </Badge>
                    <StatoBadges doc={doc} handlers={handlers} />
                  </td>
                  <td className="max-w-[200px] truncate">{doc.oggetto}</td>
                  <td className="hidden md:table-cell">{getClienteName(doc.cliente_id)}</td>
                  <td className="hidden lg:table-cell text-sm text-muted-foreground">
                    {doc.created_by_nome || '-'}
                  </td>
                  <td className={`hidden md:table-cell font-mono text-sm font-medium ${(doc.importo_totale || 0) < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                    {doc.importo_totale ? `€ ${doc.importo_totale.toFixed(2)}` : '-'}
                  </td>
                  <td className="hidden lg:table-cell font-mono text-xs">
                    <div>
                      {doc.data_documento
                        ? `${doc.data_documento.slice(8, 10)}/${doc.data_documento.slice(5, 7)}/${doc.data_documento.slice(0, 4)}`
                        : new Date(doc.created_at).toLocaleDateString('it-IT')}
                    </div>
                    {doc.tipo === 'fattura' && (() => {
                      const scad = doc.dati_fattura?.data_scadenza_pagamento;
                      if (!scad) return null;
                      const scadFmt = `${scad.slice(8, 10)}/${scad.slice(5, 7)}/${scad.slice(0, 4)}`;
                      if (doc.dati_fattura?.pagato) {
                        return <div className="text-[10px] text-emerald-600" data-testid={`scadenza-${doc.id}`}>Scad. {scadFmt}</div>;
                      }
                      const giorni = Math.floor((new Date(scad) - new Date().setHours(0, 0, 0, 0)) / 86400000);
                      const cls = giorni < 0 ? 'text-red-600 font-bold' : giorni <= 7 ? 'text-amber-600 font-semibold' : 'text-muted-foreground';
                      return (
                        <div className={`text-[10px] ${cls}`} title={giorni < 0 ? `Scaduta da ${Math.abs(giorni)} giorni` : `Scade tra ${giorni} giorni`} data-testid={`scadenza-${doc.id}`}>
                          Scad. {scadFmt}{giorni < 0 ? ' ⚠' : ''}
                        </div>
                      );
                    })()}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <AzioniDocumento doc={doc} isAdmin={isAdmin} canEdit={canEditDoc ? canEditDoc(doc) : isAdmin} canConverti={canConverti} handlers={handlers} />
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
